/**
 * API client for the Production Insights feature (Daily + Recap)
 * Supports both in-progress daily insights and wrapped production recap
 */

import {
  ProductionStatusData,
  ProductionSummary,
  ProductionInsight,
  ProductionInsightsData,
} from '../types/productionInsights';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  error?: string;
  timestamp: string;
}

const getUserEmail = (): string => {
  const email = localStorage.getItem('user_email');
  return email || 'anonymous@filmiq.ai';
};

const fetchWithAuth = async <T>(endpoint: string): Promise<ApiResponse<T>> => {
  const userEmail = getUserEmail();
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-User-Email': userEmail,
    },
  });
  
  if (!response.ok) {
    throw new Error(`API request failed: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Fetch production status (in_progress/wrapped)
 */
export const fetchProductionStatus = async (): Promise<ProductionStatusData> => {
  try {
    const response = await fetchWithAuth<ProductionStatusData>('/production-insights/status');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch production status');
  } catch (error) {
    console.error('Error fetching production status:', error);
    throw error;
  }
};

/**
 * Fetch production summary metrics
 */
export const fetchProductionSummary = async (): Promise<{
  production_status: string;
  current_day: number;
  total_days: number;
  summary: ProductionSummary;
}> => {
  try {
    const response = await fetchWithAuth<{
      production_status: string;
      current_day: number;
      total_days: number;
      summary: ProductionSummary;
    }>('/production-insights/summary');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch production summary');
  } catch (error) {
    console.error('Error fetching production summary:', error);
    throw error;
  }
};

/**
 * Fetch workload-overtime correlation insight
 */
export const fetchWorkloadOvertimeInsight = async (): Promise<ProductionInsight> => {
  try {
    const response = await fetchWithAuth<ProductionInsight>('/production-insights/workload-overtime');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch workload-overtime insight');
  } catch (error) {
    console.error('Error fetching workload-overtime insight:', error);
    throw error;
  }
};

/**
 * Fetch dropped/owed scenes insight
 */
export const fetchDroppedScenesInsight = async (): Promise<ProductionInsight> => {
  try {
    const response = await fetchWithAuth<ProductionInsight>('/production-insights/dropped-scenes');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch dropped scenes insight');
  } catch (error) {
    console.error('Error fetching dropped scenes insight:', error);
    throw error;
  }
};

/**
 * Fetch financial performance insight
 */
export const fetchFinancialInsight = async (): Promise<ProductionInsight> => {
  try {
    const response = await fetchWithAuth<ProductionInsight>('/production-insights/financial');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch financial insight');
  } catch (error) {
    console.error('Error fetching financial insight:', error);
    throw error;
  }
};

/**
 * Fetch episode efficiency insight
 */
export const fetchEpisodeEfficiencyInsight = async (): Promise<ProductionInsight> => {
  try {
    const response = await fetchWithAuth<ProductionInsight>('/production-insights/episode-efficiency');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch episode efficiency insight');
  } catch (error) {
    console.error('Error fetching episode efficiency insight:', error);
    throw error;
  }
};

/**
 * Fetch schedule accuracy patterns insight
 */
export const fetchScheduleAccuracyInsight = async (): Promise<ProductionInsight> => {
  try {
    const response = await fetchWithAuth<ProductionInsight>('/production-insights/schedule-accuracy');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch schedule accuracy insight');
  } catch (error) {
    console.error('Error fetching schedule accuracy insight:', error);
    throw error;
  }
};

/**
 * Fetch all production insights in a single request
 */
export const fetchAllProductionInsights = async (): Promise<ProductionInsightsData> => {
  try {
    const response = await fetchWithAuth<ProductionInsightsData>('/production-insights/all');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch all production insights');
  } catch (error) {
    console.error('Error fetching all production insights:', error);
    throw error;
  }
};
